/**
 * ConsonantPracticeSession — Main session screen for the 6-prompt guided flow
 *
 * Layout:
 * - Mobile/portrait: Camera top (35vh), content bottom
 * - Desktop/landscape (md+): Camera left (38%), content right (62%)
 *
 * Flow per card (prescribed order):
 *   awaiting_scan → VP1 → VP2 (pre-video → videos → post-video) → VP3
 *   → VP3 capture (mic) → VP4a (video) → VP4b → VP5a (video) → VP5b → VP6 (mastery)
 *
 * @see useConsonantReviewFlow.ts for state machine
 * @see consonantReviewService.ts for audio builders
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, CheckCircle2, AlertCircle, Volume2, RotateCcw, ChevronRight, Eye, Loader2, BookOpen, Trophy, Mic } from 'lucide-react';
import { QRCodeScanner } from '@/components/scanner';
import { DigitalLetterCard } from './DigitalLetterCard';
import { useConsonantReviewFlow, type SessionStep } from './useConsonantReviewFlow';
import { recordConsonantPracticeSession } from '@/services/learningProgressService';
import { CARD_ORDER } from '@/services/consonantReviewService';
import { useMicStream } from '@/hooks/useMicStream';
import { useSessionRecording } from '@/hooks/useSessionRecording';
import { usePhonemeCapture } from '@/lib/voice-agent/hooks/usePhonemeCapture';
import { supabase } from '@/lib/supabase/client';
import { useTranslation } from '@/lib/i18n';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

interface ConsonantPracticeSessionProps {
  childId?: string;
  childName: string;
  recordingEnabled?: boolean;
  onEndSession: () => void;
}

// Steps where Continue button is shown
const CONTINUE_STEPS: Set<SessionStep> = new Set([
  'prompt1', 'prompt2_pre_video', 'prompt2_post_video', 'prompt3',
  'prompt3_capture', 'prompt4b', 'prompt5b',
  'prompt2_video', 'prompt4a_video', 'prompt5a_video',
]);

// Steps where Show Me How button is shown
const SHOW_ME_HOW_STEPS: Set<SessionStep> = new Set(['prompt4b', 'prompt5b']);

// Steps where Restart button is shown
const RESTART_STEPS: Set<SessionStep> = new Set([
  'prompt1', 'prompt2_pre_video', 'prompt2_video', 'prompt2_post_video',
  'prompt3', 'prompt4a', 'prompt4a_video', 'prompt4b',
  'prompt5a', 'prompt5a_video', 'prompt5b',
]);

// ============================================================================
// Video Player Sub-Component
// ============================================================================

function SessionVideoPlayer({
  videoPath,
  crop,
  onEnded,
}: {
  videoPath: string | undefined;
  crop?: 'left' | 'right';
  onEnded: () => void;
}) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    if (!videoPath) {
      setVideoUrl(null);
      return;
    }
    const { data } = supabase.storage
      .from('consonant-cards')
      .getPublicUrl(videoPath);
    if (data?.publicUrl) {
      setVideoUrl(data.publicUrl);
      setVideoError(false);
    }
  }, [videoPath]);

  // Wait for canplay before calling play() to avoid race condition
  useEffect(() => {
    const video = videoRef.current;
    if (!videoUrl || !video) return;

    const tryPlay = () => {
      video.play().catch(() => setVideoError(true));
    };

    if (video.readyState >= 3) {
      tryPlay();
    } else {
      video.addEventListener('canplay', tryPlay, { once: true });
      return () => video.removeEventListener('canplay', tryPlay);
    }
  }, [videoUrl]);

  const shouldAutoAdvance = !videoPath || videoError;

  useEffect(() => {
    if (!shouldAutoAdvance) return;
    const timer = setTimeout(onEnded, 500);
    return () => clearTimeout(timer);
  }, [shouldAutoAdvance, onEnded]);

  if (shouldAutoAdvance) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-white/40 text-sm">{t('Video not available')}</p>
      </div>
    );
  }

  if (!videoUrl) return null;

  if (crop) {
    return (
      <div className="w-full max-h-full overflow-hidden rounded-2xl">
        <video
          ref={videoRef}
          src={videoUrl}
          className="block w-[200%] max-w-none h-auto"
          style={{ marginLeft: crop === 'right' ? '-100%' : '0' }}
          playsInline
          onEnded={onEnded}
          onError={() => {
            setVideoError(true);
            onEnded();
          }}
        />
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      className="max-w-full max-h-full object-contain rounded-2xl"
      playsInline
      onEnded={onEnded}
      onError={() => {
        setVideoError(true);
        onEnded();
      }}
    />
  );
}

// ============================================================================
// Component
// ============================================================================

export const ConsonantPracticeSession: React.FC<ConsonantPracticeSessionProps> = ({
  childId,
  childName,
  recordingEnabled = false,
  onEndSession,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const sessionStartTime = useRef<Date>(new Date());
  const sessionIdRef = useRef<string | null>(null);
  const { t } = useTranslation();

  // Shared mic stream — single permission grant for both recording + phoneme capture
  const { streamRef: micStreamRef, acquire: acquireMic, release: releaseMic } = useMicStream();

  // Recording integration (stream passed at call time, not at init)
  const {
    isRecording,
    startRecording,
    stopRecording,
    error: recordingError,
  } = useSessionRecording({ enabled: recordingEnabled, childId });

  // Phoneme detection (uses shared stream for capture)
  const {
    isCapturing: isPhonemeCapturing,
    startCapture: startPhonemeCapture,
    stopCapture: stopPhonemeCapture,
    verifyPhoneme,
    preload: preloadPhoneme,
  } = usePhonemeCapture();
  const phonemeResultsRef = useRef<Map<string, { detected: boolean; similarity: number }>>(new Map());
  const capturePhonemeRef = useRef<string | null>(null);

  // Acquire mic once on mount, pass stream to startRecording at call time
  useEffect(() => {
    (async () => {
      const stream = await acquireMic();
      if (stream && recordingEnabled) {
        startRecording(stream);
      }
    })();
    return () => releaseMic();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Surface recording/mic errors as toast so parents know if recording failed
  useEffect(() => {
    if (recordingError) {
      toast.error('Session recording unavailable — microphone access may have been denied.');
    }
  }, [recordingError]);

  const {
    step,
    scannedCard,
    isPlaying,
    activeVideo,
    scanFeedback,
    currentCardIndex,
    expectedPhoneme,
    completedCards,
    handleQRScan,
    handleQRError,
    handleRestart,
    handleContinue,
    handleShowMeHow,
    handleMasterySelection,
    handleVideoEnded,
    phonemesScanned,
    stepLabels,
  } = useConsonantReviewFlow(childId, childName);

  // Phoneme capture: start when entering prompt3_capture, stop+verify when leaving
  useEffect(() => {
    if (step === 'prompt3_capture' && scannedCard && !capturePhonemeRef.current) {
      capturePhonemeRef.current = scannedCard.phoneme;
      preloadPhoneme(scannedCard.phoneme);
      startPhonemeCapture(micStreamRef.current);
    } else if (capturePhonemeRef.current && step !== 'prompt3_capture') {
      const phoneme = capturePhonemeRef.current;
      capturePhonemeRef.current = null;
      (async () => {
        try {
          const blob = await stopPhonemeCapture();
          if (blob && blob.size > 0) {
            const result = await verifyPhoneme(phoneme, blob);
            phonemeResultsRef.current.set(phoneme, {
              detected: result.isCorrect,
              similarity: result.similarity,
            });
          }
        } catch (err) {
          console.error('[ConsonantPractice] Phoneme capture error:', err);
        }
      })();
    }
  }, [step, scannedCard, startPhonemeCapture, stopPhonemeCapture, verifyPhoneme, preloadPhoneme, micStreamRef]);

  // Save session and end — error-checked at every step, consolidated toast after
  const handleEndSession = useCallback(async () => {
    if (!childId || phonemesScanned.current.length === 0) {
      if (recordingEnabled) {
        await stopRecording();
      }
      onEndSession();
      return;
    }

    setIsSaving(true);
    const errors: string[] = [];

    try {
      const durationSeconds = Math.round(
        (new Date().getTime() - sessionStartTime.current.getTime()) / 1000
      );

      let recordingPath: string | null = null;
      if (recordingEnabled) {
        recordingPath = await stopRecording();
        if (!recordingPath && recordingEnabled) {
          errors.push('recording upload');
        }
      }

      const result = await recordConsonantPracticeSession(childId, durationSeconds, phonemesScanned.current);

      if (!result.success || !result.sessionId) {
        errors.push('session creation');
        console.error('[ConsonantPractice] Session creation failed:', result.error);
      } else {
        const sessionId = result.sessionId;
        sessionIdRef.current = sessionId;

        if (recordingPath) {
          const { error: urlError } = await supabase
            .from('voice_agent_sessions')
            .update({
              recording_url: recordingPath,
              recording_duration_seconds: durationSeconds,
            })
            .eq('id', sessionId);
          if (urlError) {
            errors.push('recording link');
            console.error('[ConsonantPractice] Recording URL update failed:', urlError.message);
          }
        }

        const details = phonemesScanned.current.map((p, idx) => ({
          session_id: sessionId,
          child_id: childId,
          phoneme: p.phoneme,
          skill_id: p.skillId || null,
          card_order: idx + 1,
          parent_feedback: p.masteryFeedback || 'learning',
          phoneme_detection_result: phonemeResultsRef.current.get(p.phoneme)
            ? { detected: phonemeResultsRef.current.get(p.phoneme)!.detected, similarity: phonemeResultsRef.current.get(p.phoneme)!.similarity }
            : null,
        }));

        if (details.length > 0) {
          const { error: detailsError } = await supabase
            .from('consonant_session_details')
            .insert(details);
          if (detailsError) {
            errors.push('card details');
            console.error('[ConsonantPractice] Details insert failed:', detailsError.message);
          }
        }
      }
    } catch (error) {
      errors.push('session save');
      console.error('[ConsonantPractice] Error saving session:', error);
    } finally {
      setIsSaving(false);
      if (errors.length > 0) {
        toast.error(`Session saved with issues (${errors.join(', ')}). Progress may be incomplete.`);
      }
      onEndSession();
    }
  }, [childId, onEndSession, phonemesScanned, recordingEnabled, stopRecording]);

  // Resolve video path and optional crop direction for active video
  const { activeVideoPath, videoCrop } = (() => {
    if (!activeVideo || !scannedCard) return { activeVideoPath: undefined, videoCrop: undefined };
    switch (activeVideo) {
      case 'lowercase_image_live': return { activeVideoPath: scannedCard.lowercaseImageLiveVideo, videoCrop: undefined };
      case 'uppercase_image_live': return { activeVideoPath: scannedCard.uppercaseImageLiveVideo, videoCrop: undefined };
      case 'lowercase_tracing': return { activeVideoPath: scannedCard.coloringVideo, videoCrop: 'left' as const };
      case 'uppercase_tracing': return { activeVideoPath: scannedCard.coloringVideo, videoCrop: 'right' as const };
      default: return { activeVideoPath: undefined, videoCrop: undefined };
    }
  })();

  const isVideoStep = !!activeVideo;
  const showCamera = step === 'awaiting_scan' || (!isVideoStep && step !== 'prompt6' && step !== 'session_complete');
  const stepLabel = stepLabels[step];

  return (
    <div className="flex flex-col bg-black" style={{ minHeight: 'calc(100dvh - 9rem)' }}>
      {/* Session toolbar */}
      <header className="flex items-center justify-between px-4 py-2 border-b border-white/10 flex-shrink-0">
        <div className="text-left">
          <span className="text-white/60 text-sm font-medium block">
            {t('Card')} {Math.min(currentCardIndex + 1, CARD_ORDER.length)} {t('of')} {CARD_ORDER.length}
            {expectedPhoneme && step === 'awaiting_scan' && (
              <span className="text-white/40"> — {t('Letter')} {expectedPhoneme.toUpperCase()}</span>
            )}
          </span>
          {completedCards.length > 0 && (
            <span className="text-white/40 text-xs">
              {completedCards.length} {t('completed')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {recordingEnabled && (
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${
              recordingError
                ? 'bg-[#FF9F0A]/15 border border-[#FF9F0A]/30'
                : isRecording
                  ? 'bg-[#FF453A]/15 border border-[#FF453A]/30'
                  : 'bg-white/5 border border-white/10'
            }`}>
              <Mic className={`w-3 h-3 ${
                recordingError ? 'text-[#FF9F0A]' : isRecording ? 'text-[#FF453A] animate-pulse' : 'text-white/30'
              }`} />
              <span className={`text-xs font-medium ${
                recordingError ? 'text-[#FF9F0A]' : isRecording ? 'text-[#FF453A]' : 'text-white/30'
              }`}>
                {recordingError ? t('MIC ERR') : isRecording ? t('REC') : t('OFF')}
              </span>
            </div>
          )}
          <button
            onClick={handleEndSession}
            disabled={isSaving}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FF453A]/10 hover:bg-[#FF453A]/20 text-[#FF453A] text-sm transition-colors min-h-[44px] disabled:opacity-50"
          >
            <span>{isSaving ? t('Saving...') : t('End Session')}</span>
            <X className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        {/* Camera/QR Scanner */}
        {showCamera && (
          <div className="relative h-[35vh] md:h-auto md:flex-none md:w-[38%] min-h-0 bg-black flex-shrink-0">
            <QRCodeScanner
              onScan={handleQRScan}
              onError={handleQRError}
              onClose={() => {}}
              useFrontCamera={true}
              cooldownMs={1500}
              showCloseButton={false}
            />

            {/* QR Targeting Guide */}
            {step === 'awaiting_scan' && !scanFeedback && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]">
                <div className="w-36 h-36 relative">
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white/50 rounded-tl" />
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white/50 rounded-tr" />
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white/50 rounded-bl" />
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white/50 rounded-br" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white/30 text-[10px] font-medium tracking-wider uppercase">QR</span>
                  </div>
                </div>
              </div>
            )}

            {/* Scan Feedback Overlay */}
            {scanFeedback ? (
              <div className="absolute bottom-2 left-2 right-2 flex justify-center z-10">
                <div className={`px-3 py-2 rounded-xl flex items-center gap-2 w-full backdrop-blur-sm ${
                  scanFeedback.success
                    ? 'bg-[#30D158]/20 border border-[#30D158]/30'
                    : 'bg-[#FF453A]/20 border border-[#FF453A]/30'
                }`}>
                  {scanFeedback.success ? (
                    <CheckCircle2 className="w-4 h-4 text-[#30D158] flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-[#FF453A] flex-shrink-0" />
                  )}
                  <span className="text-white text-xs">{scanFeedback.message}</span>
                </div>
              </div>
            ) : (
              <div className="absolute bottom-2 left-2 right-2 flex justify-center pointer-events-none z-10">
                <div className="px-3 py-1.5 rounded-lg bg-black/60 backdrop-blur-sm">
                  <p className="text-white/60 text-xs text-center flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#30D158] animate-pulse" />
                    {step === 'awaiting_scan'
                      ? `${t('Find letter')} ${expectedPhoneme.toUpperCase()} ${t('and scan it')}`
                      : t('Scan another card anytime')}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 flex flex-col min-h-0 border-t md:border-t-0 md:border-l border-white/10">

          {/* VP6 — Mastery Selection (replaces old modal) */}
          {step === 'prompt6' && scannedCard && (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="w-full max-w-md space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-bold text-white mb-2">
                    {t('How did')} {childName} {t('do with')} {scannedCard.displayName}?
                  </h2>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => handleMasterySelection('learning')}
                    className="w-full flex items-start gap-4 px-6 py-5 rounded-2xl bg-[#FF9F0A]/15 border border-[#FF9F0A]/30 hover:bg-[#FF9F0A]/25 active:scale-[0.98] transition-all text-left"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#FF9F0A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <BookOpen className="w-6 h-6 text-[#FF9F0A]" />
                    </div>
                    <div>
                      <p className="text-[#FF9F0A] font-semibold text-lg">{t('Still Learning')}</p>
                      <p className="text-white/50 text-sm mt-1">
                        {t('This card will be included in the next learning session.')}
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => handleMasterySelection('mastered')}
                    className="w-full flex items-start gap-4 px-6 py-5 rounded-2xl bg-[#30D158]/15 border border-[#30D158]/30 hover:bg-[#30D158]/25 active:scale-[0.98] transition-all text-left"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#30D158]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Trophy className="w-6 h-6 text-[#30D158]" />
                    </div>
                    <div>
                      <p className="text-[#30D158] font-semibold text-lg">{t('Mastered')}</p>
                      <p className="text-white/50 text-sm mt-1">
                        {t("This card will not be included in the next learning session.")}
                        {' '}{childName}{t("'s mastery will be tested in upcoming progressive and cumulative tests")}
                        {' '}{t("(so don't worry,")} {childName} {t("will see it many more times)!")}
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Session Complete */}
          {step === 'session_complete' && (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#30D158]/20 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-[#30D158]" />
                </div>
                <h2 className="text-xl font-bold text-white">{t('Session Complete!')}</h2>
                <p className="text-white/60 text-sm">
                  {childName} {t('practiced')} {completedCards.length} {completedCards.length !== 1 ? t('cards') : t('card')}.
                </p>
                <button
                  onClick={handleEndSession}
                  disabled={isSaving}
                  className="px-6 py-3 bg-[#0A84FF] hover:bg-[#0A84FF]/80 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {isSaving ? t('Saving...') : t('Finish')}
                </button>
              </div>
            </div>
          )}

          {/* Video Player (for video steps) */}
          {isVideoStep && step !== 'prompt6' && step !== 'session_complete' && (
            <div className="flex-1 flex items-center justify-center min-h-0 overflow-hidden p-4">
              <SessionVideoPlayer
                videoPath={activeVideoPath}
                crop={videoCrop}
                onEnded={handleVideoEnded}
              />
            </div>
          )}

          {/* Card Display (non-video, non-prompt6, non-complete steps) */}
          {!isVideoStep && step !== 'prompt6' && step !== 'session_complete' && (
            <div className="flex-1 flex items-center justify-center min-h-0 overflow-hidden relative">
              {step === 'awaiting_scan' && !scannedCard ? (
                <div className="text-center p-6 space-y-3">
                  <p className="text-white text-lg font-semibold">
                    {t('Find letter')} <span className="text-[#0A84FF]">{expectedPhoneme.toUpperCase()}</span> {t('and scan the card')}
                  </p>
                  <p className="text-white/40 text-sm">
                    {t('Hold the QR code up to the camera')}
                  </p>
                </div>
              ) : (
                <DigitalLetterCard
                  imagePath={scannedCard?.legacyImagePath || null}
                  cardImages={scannedCard?.cardImages}
                  imageType={step === 'prompt2_pre_video' || step === 'prompt2_post_video' ? 'illustrated' : 'plain'}
                  cropSide={
                    step === 'prompt4a' || step === 'prompt4b' ? 'left'
                    : step === 'prompt5a' || step === 'prompt5b' ? 'right'
                    : null
                  }
                  phoneme={scannedCard?.phoneme}
                  displayName={scannedCard?.displayName}
                />
              )}

              {/* Step Badge */}
              {stepLabel && scannedCard && (
                <div className="absolute top-3 left-3 right-20 flex">
                  <div className="px-3 py-1 rounded-full bg-[#5E5CE6]/20 backdrop-blur-sm border border-[#5E5CE6]/30">
                    <span className="text-[#BF5AF2] text-xs font-medium">{stepLabel}</span>
                  </div>
                </div>
              )}

              {/* Playing Indicator */}
              {isPlaying && (
                <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-[#0A84FF]/20 backdrop-blur-sm border border-[#0A84FF]/30 flex items-center gap-1.5">
                  <Volume2 className="w-3.5 h-3.5 text-[#0A84FF] animate-pulse" />
                  <span className="text-[#0A84FF] text-xs font-medium">{t('Playing...')}</span>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex-shrink-0 px-4 pb-4 pt-2 min-h-[56px]">
            {step !== 'awaiting_scan' && step !== 'prompt6' && step !== 'session_complete' && scannedCard && (
              <div className="flex gap-2 sm:gap-3 justify-center flex-wrap">
                {/* Restart */}
                {RESTART_STEPS.has(step) && (
                  <button
                    onClick={handleRestart}
                    disabled={isPlaying && !isVideoStep}
                    className="flex items-center gap-1.5 px-4 py-2 sm:px-5 sm:py-3 bg-[#FF9F0A]/15 hover:bg-[#FF9F0A]/25 border border-[#FF9F0A]/30 text-[#FF9F0A] rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all disabled:opacity-50 min-h-[44px]"
                  >
                    <RotateCcw className="w-4 h-4" />
                    {t('Restart')}
                  </button>
                )}

                {/* Show Me How (4b, 5b only) */}
                {SHOW_ME_HOW_STEPS.has(step) && (
                  <button
                    onClick={handleShowMeHow}
                    disabled={isPlaying}
                    className="flex items-center gap-1.5 px-4 py-2 sm:px-5 sm:py-3 bg-[#5E5CE6]/15 hover:bg-[#5E5CE6]/25 border border-[#5E5CE6]/30 text-[#BF5AF2] rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all disabled:opacity-50 min-h-[44px]"
                  >
                    <Eye className="w-4 h-4" />
                    {t('Show Me How')}
                  </button>
                )}

                {/* Continue */}
                {CONTINUE_STEPS.has(step) && (
                  <button
                    onClick={handleContinue}
                    disabled={isPlaying && !isVideoStep}
                    className="flex items-center gap-1.5 px-4 py-2 sm:px-5 sm:py-3 bg-[#30D158]/15 hover:bg-[#30D158]/25 border border-[#30D158]/30 text-[#30D158] rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all disabled:opacity-50 min-h-[44px]"
                  >
                    {t('Continue')}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}

            {/* Awaiting scan state */}
            {step === 'awaiting_scan' && (
              <div className="text-center">
                <p className="text-white/40 text-xs">
                  {t('Point camera at the QR code on the')} {expectedPhoneme.toUpperCase()} {t('card')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsonantPracticeSession;
